"use client";

import { IBaseLabelValue } from "@/interfaces";
import { usePathname, useRouter } from "next/navigation";
import { cn, formatNumberWithDots, generateBillPaymentCallbackUrl } from "@/lib";
import { routes } from "@/routes";
import { useTranslations } from "next-intl";
import React, { useRef } from "react";
import PdfTemplate from "@/components/pdfTemplates/pdfTemplate";
import { IRawPurchasedGasInfoInquiryResData } from "@/interfaces/inquiries/governmental";
import { CustomButton, FlexBetween, FlexCenter, FlexCol } from "@/components";
import InputDataContent from "@/components/inquiryDetails/inputDataContent";
import DownloadPdf from "@/components/pdfTemplates/downloadPdf";
import { usePayFinesMutation } from "@/hooks";
import { useSession } from "next-auth/react";
import { useAppStore } from "@/store";
import { IPayFinesPayload } from "@/interfaces/inquiries/vehicle";
import { BdCopy } from "solar-icon-react/bd";
import { useToastStore } from "@/store/toastStore";

const GasBillInfoInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedGasInfoInquiryResData;
}) => {
  const t = useTranslations("serviceResponseDetails");
  const { data: session } = useSession();
  const {
    isPending: isPaySubmitting,
    mutate: PayFineFn,
    error: paySubmittingError,
    reset: resetPay,
  } = usePayFinesMutation(session?.user.token);
  const router = useRouter();
  const response = data.data.data;

  if (response.message) {
    return (
      <div className="shadow-main relative w-full rounded-2xl bg-white p-8">
        <div
          className="mx-auto max-w-[500px] rounded-lg p-4 text-center text-white"
          style={{ backgroundColor: "#5a4bd2" }}
        >
          {response.message}
        </div>
      </div>
    );
  }
  if (!response) {
    return (
      <div className="shadow-main relative w-full rounded-2xl bg-white p-8">
        <div
          className="mx-auto max-w-[500px] rounded-lg p-4 text-center text-white"
          style={{ backgroundColor: "#5a4bd2" }}
        >
          برای این شناسه قبضی صادر نشده
        </div>
      </div>
    );
  }
  const { bill_id } = data.data.input;
  const formatPrice = (value?: string | number) =>
    value ? `${formatNumberWithDots(Number(value) / 10)} تومان` : "-";
  const inputContent: IBaseLabelValue[] = [
    {
      label: t("electricityBill.inputLabel"),
      value: response.billID || "",
    },
  ];
  const resultContent: IBaseLabelValue[] = [
    {
      label: t("gasBill.addressCode"),
      value: response.info.AddressCode || "-",
    },
    {
      label: t("gasBill.areaCode"),
      value: response.info.AreaCode || "-",
    },
    {
      label: t("gasBill.capacity"),
      value: response.info.Capacity || "-",
    },
    {
      label: t("gasBill.city"),
      value: response.info.City || "-",
    },
    {
      label: t("gasBill.counterActivity"),
      value: response.info.CounterActivity || "-",
    },
    {
      label: t("gasBill.currentCounterDigit"),
      value: response.info.CurrentCounterDigit || "-",
    },
    {
      label: t("gasBill.currentReadingDate"),
      value: response.info.CurrentReadingDate || "-",
    },
    {
      label: t("gasBill.fileNumber"),
      value: response.info.FileNumber || "-",
    },
    {
      label: t("gasBill.debtCount"),
      value: response.info.DebtCount || "-",
    },
    {
      label: t("gasBill.deductionAmount1000Rial"),
      value: response.info.DeductionAmount1000Rial || "-",
    },
    {
      label: t("gasBill.gasConsumptionAmount"),
      value: formatPrice(response.info.GasConsumptionAmount),
    },
    {
      label: t("gasBill.group"),
      value: response.info.Group || "-",
    },
    {
      label: t("gasBill.insurance"),
      value: response.info.Insurance || "-",
    },
    {
      label: t("gasBill.meterSerial"),
      value: response.info.MeterSerial || "-",
    },
    {
      label: t("gasBill.otherDebt"),
      value: response.info.OtherDebt || "-",
    },
    {
      label: t("gasBill.ownerName"),
      value: response.info.OwnerName || "-",
    },
    {
      label: t("gasBill.previousCounterDigit"),
      value: response.info.PreviousCounterDigit || "-",
    },
    {
      label: t("gasBill.previousReadingDate"),
      value: response.info.PreviousReadingDate || "-",
    },
    {
      label: t("gasBill.remainingAmount1000Rial"),
      value: response.info.RemainingAmount1000Rial || "-",
    },
    {
      label: t("gasBill.remainingDebt"),
      value: response.info.RemainingDebt || "-",
    },
    {
      label: t("gasBill.remainingPreviousInvoice"),
      value: response.info.RemainingPreviousInvoice || "-",
    },
    {
      label: t("gasBill.serialNumber"),
      value: response.info.SerialNumber || "-",
    },
    {
      label: t("gasBill.standardConsumption"),
      value: response.info.StandardConsumption || "-",
    },
    {
      label: t("gasBill.subscription"),
      value: response.info.Subscription || "-",
    },
    {
      label: t("gasBill.subscriptionNumber"),
      value: response.info.SubscriptionNumber || "-",
    },
    {
      label: t("gasBill.taxes"),
      value: formatPrice(response.info.Taxes),
    },
    {
      label: t("gasBill.villageGasTaxes"),
      value: formatPrice(response.info.VillageGasTaxes),
    },
    {
      label: t("gasBill.unitCount"),
      value: response.info.UnitCount || "-",
    },
    {
      label: t("gasBill.useType"),
      value: response.info.UseType || "-",
    },
    {
      label: t("gasBill.amount"),
      value: formatPrice(response.amount),
    },
    {
      label: t("electricityBill.inputLabel"),
      value: response.billID || "",
      type: "copiable",
    },
    {
      label: t("electricityBill.payID"),
      value: response.payID || "",
      type: "copiable",
    },
  ];
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const pathName = usePathname();
  const { setPreviousPath } = useAppStore();
  const { setOpenToast } = useToastStore();
  const handlePayBillSubmission = () => {
    const payload: IPayFinesPayload = {
      amount: response.amount,
      callback_url: generateBillPaymentCallbackUrl(pathName, (data.data as any).purchase_id),
      bill_id: Number(response.billID),
      pay_id: Number(response.payID),
    };
    PayFineFn(
      { payload },
      {
        onSuccess: (data) => {
          if (data.data) {
            setPreviousPath(pathName);
            window.location.href = data.data?.payment_link;
          }
        },
      },
    );
  };
  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setOpenToast({
        variant: "default",
        message: "کپی شد",
      });
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };
  return (
    <div className="shadow-main relative basis-[500px] rounded-2xl bg-linear-to-b from-rose-500 to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-8 text-center font-bold lg:text-lg">
          استعلام قبض گاز با جزئیات
        </h2>

        <InputDataContent content={inputContent} />
        <FlexBetween className="mb-3 items-center">
          <h2 className="text-center text-lg font-bold">
            {t("postalCodeInquiry.resultTitle")}
          </h2>
          <DownloadPdf
            id="gas-inquiry-download-pdf"
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
            fileName={`قبض گاز - ${bill_id}`}
          />
        </FlexBetween>
        <ul
          className={cn(
            "shadow-main mb-10 flex flex-col gap-6 rounded-xl p-4 py-8 sm:p-8",
          )}
        >
          {resultContent.map((item, index) => (
            <li
              key={index}
              className={cn(
                "flex-between items-start gap-6 text-xs sm:text-sm md:gap-8 lg:text-base",
                item.value === "-" ? "hidden" : "flex",
              )}
            >
              <span className="min-w-max">{item.label}</span>
              <div className="flex items-center gap-1">
                <span className="text-start" style={{ color: "#5a2bd2" }}>
                  {item.value}
                </span>
                {item.type === "copiable" && (
                  <BdCopy
                    onClick={() => copy(item.value)}
                    className="text-primary-main cursor-pointer text-base"
                  />
                )}
              </div>
            </li>
          ))}
          {response?.amount != 0 ? (
            <CustomButton
              id="details-water-inquiry-back"
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
              onClick={handlePayBillSubmission}
              isLoading={isPaySubmitting}
            >
              پرداخت {formatPrice(response.amount)}
            </CustomButton>
          ) : (
            <p className="bg-success-main mx-auto w-full max-w-[200px] rounded-lg px-3 py-2 text-center text-white">
              تسویه شده
            </p>
          )}
        </ul>
        <FlexCenter className="mt-4 flex flex-col justify-center md:flex-row md:gap-6">
          <DownloadPdf
            id="gas-inquiry-download-pdf"
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
            fileName={`قبض گاز - ${bill_id}`}
            className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            showIcon={false}
            labelClass="text-xs lg:text-sm xl:text-base font-medium"
          />
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-gas-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-gas-inquiry-new-request"
              onClick={() =>
                router.push(routes.services.Gas_Bill_Info_inquiry.rewritePath)
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
      </FlexCol>
      {/* This div will be used for PDF generation - DetailCreditCard style */}
      <div
        ref={pdfContentRef}
        style={{
          position: "fixed",
          left: "-9999px",
          top: "-9999px",
          width: "800px",
          fontFamily: "IRANYekan, sans-serif",
          margin: "2rem auto",
        }}
      >
        <PdfTemplate
          tableTitle="اطلاعات قبض گاز"
          resultContent={resultContent}
          code={data.code}
          dateTime={data.created_at}
        />
      </div>
    </div>
  );
};

export default GasBillInfoInquiryDetails;
